import React, { CSSProperties, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { IndexableObject } from "./Helpers";
import { getAllComponentNames, getComponentData, getProjectInfo, saveComponentTree } from "./HelpersProject";
import { ComponentEditorContext } from "./ComponentEditorContext";
import { Box, Typography } from "@mui/material";
import { useImmer } from "use-immer";
import { ImageInfo, ImageSrc } from "./ImageInfo";
import { ProjectInfo } from "./ProjectInfo";
import { TreeView } from "./TreeView";
import { ModalEditorDisplay } from "./ModalEditorDisplay";
import { TreeDataContext, TreeModifyContext } from "./TreeDataContext";
import { useParams } from "react-router-dom";
import { useAutosave } from "react-autosave";
import { PathProvider } from "./PathInfo";
import "./ComponentEditor.css";
import ImageWithFallback from "./ImageWithFallback";
import { importAll } from "../gametable/tableUtility";
import CgoCardSide from "../CgoCard";

function ComponentEditorTab() {
  let params = useParams();
  const projectSlug = useMemo(() => {
    return params.projectSlug ? params.projectSlug : "";
  }, [params.projectSlug]);
  const [allImages, setImages] = useState<any | null>(null);
  const [projectInfo, setProjectInfo] = useImmer<ProjectInfo | undefined>(undefined);
  const [treeData, setTreeData] = useImmer<IndexableObject | null>(null);

  useAutosave({
    data: treeData,
    onSave: (data) => {
      if (data !== null) {
        console.log("Saving data", data);
        return saveComponentTree(projectSlug, true, data);
      }
    },
    saveOnUnmount: true,
    interval: 500,
  });

  useEffect(() => {
    getComponentData(projectSlug, true)
      .catch(() => console.log("Could not load project data"))
      .then((r) => setTreeData(r));
  }, [projectSlug, setTreeData]);

  useEffect(() => {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    const loadImages = async () => {
      if (!params.projectSlug) return;

      // Load images asynchronously
      let importedImages = await importAll(projectSlug);
      importedImages.empty = {};
      importedImages.empty.default = "./empty.png";
      setImages(importedImages);
      console.log("Images loaded", importedImages);
    };
    loadImages();
  }, [projectSlug]);

  useEffect(() => {
    getProjectInfo(projectSlug).then((r) => setProjectInfo(r));
  }, [projectSlug, setProjectInfo]);

  const getImageSrc = useCallback(
    (imageInfo: ImageSrc): string | undefined => {
      if (imageInfo.value === undefined) {
        return undefined;
      }
      if (localStorage.getItem("server-env") === "dev") {
        if (allImages && allImages[imageInfo.value]) {
          return allImages[imageInfo.value]["default"];
        }
      } else {
        return "https://storage.googleapis.com/cgo-projects/" + projectSlug + "/assets/" + imageInfo.value;
      }
    },
    [allImages, projectSlug],
  );

  const getVisual = useCallback(
    (
      imageInfo: ImageInfo | undefined,
      setRef: (instance: HTMLElement | null) => void,
      style?: CSSProperties,
      innerStyle?: CSSProperties,
      className?: string,
      onClick?: (event: React.MouseEvent<HTMLElement>) => void,
    ): ReactElement | undefined => {
      function getBackgroundStyle(oldStyle: CSSProperties | undefined) {
        let newStyle = { ...oldStyle };
        newStyle.userSelect = "none";
        return newStyle;
      }

      function getInnerStyle(oldStyle: CSSProperties | undefined) {
        return { ...oldStyle };
      }

      const clickHandler = (event: React.MouseEvent<HTMLElement>) => {
        if (onClick) {
          onClick(event);
        }
      };

      if (imageInfo === undefined) {
        return undefined;
      }
      if (imageInfo.type === "reactImg") {
        return (
          <Box
            ref={(instance) => setRef(instance as HTMLElement)}
            className={className}
            style={getBackgroundStyle(style)}
            onClick={clickHandler}
          >
            <CgoCardSide
              style={getInnerStyle(innerStyle)}
              printing={"onePerSheet"}
              deckInfo={imageInfo.deckInfo}
              cardId={imageInfo.cardId}
              globalAssets={projectInfo?.globalAssets}
            />
          </Box>
        );
      } else if (imageInfo.type === "src") {
        let imgSrc = getImageSrc(imageInfo);
        return (
          <Box
            ref={(instance) => setRef(instance as HTMLElement)}
            className={className}
            style={getBackgroundStyle(style)}
            onClick={clickHandler}
          >
            <ImageWithFallback
              key={imgSrc}
              src={imgSrc}
              alt={imageInfo.value}
              style={getInnerStyle(innerStyle)}
              fallbackSrc={"../../../" + allImages.empty.default}
            />
          </Box>
        );
      } else {
        return undefined;
      }
    },
    [getImageSrc],
  );

  const saveChangesAtPath = useCallback(
    (newValue: any, path: string[]) => {
      let finalPath = path;

      console.log("finalPath: " + finalPath);
      console.log("newValue: " + JSON.stringify(newValue));

      setTreeData((draft) => {
        if (!draft) {
          return newValue;
        }
        let currentNode = draft;

        if (finalPath.length === 0) {
          return newValue; // Directly return the new value if there's no path (e.g. setting the root)
        }

        // Traverse the path to get to the correct node, creating nodes as needed
        for (let i = 0; i < finalPath.length - 1; i++) {
          let pathKey: string | number = finalPath[i];
          // If pathKey is a number, use array indexing instead of object keys
          if (!isNaN(Number(pathKey))) {
            pathKey = Number(pathKey);
          }
          if (!currentNode[pathKey]) {
            currentNode[pathKey] = {}; // Initialize a new node if it doesn't exist
          }
          currentNode = currentNode[pathKey];
        }

        // Set the new value at the last key of the path
        currentNode[finalPath[finalPath.length - 1]] = newValue;
      });
    },
    [setTreeData],
  );

  const removeAtPath = useCallback(
    (path: string[]) => {
      console.log("delete path: " + path);

      setTreeData((draft) => {
        if (!draft) {
          return;
        }
        let currentNode = draft;

        // Traverse the path to get to the correct node
        for (let i = 0; i < path.length - 1; i++) {
          let pathKey = path[i];
          if (!currentNode[pathKey]) {
            return; // Return if the path doesn't exist
          }

          // If the second to last key is an array and the last key is a number, remove the item at the index of the number
          if (i === path.length - 2 && Array.isArray(currentNode[pathKey]) && !isNaN(Number(path[path.length - 1]))) {
            const targetIndex = Number(path[path.length - 1]);
            currentNode[pathKey].splice(targetIndex, 1);
            return;
          }

          currentNode = currentNode[pathKey];
        }

        // Delete the node at the last key of the path
        delete currentNode[path[path.length - 1]];
      });
    },
    [setTreeData],
  );

  const addGamePiece = useCallback(
    (gamePieceName: string, gamePieceValue: any) => {
      setTreeData((draft) => {
        if (!draft) {
          return;
        }
        // Add the new game piece as the first property in the object
        let modifiedTreeData = {} as IndexableObject;
        modifiedTreeData[gamePieceName] = gamePieceValue;
        for (let key in draft) {
          modifiedTreeData[key] = draft[key];
        }
        return modifiedTreeData;
      });
    },
    [setTreeData],
  );

  const basePath = useMemo<string[]>(() => {
    return [];
  }, []);

  const componentEditorContext = useMemo(
    () => ({
      allImages,
      projectName: projectSlug,
      projectInfo,
      getImageSrc,
      getVisual,
    }),
    [allImages, projectSlug, projectInfo, getImageSrc, getVisual],
  );

  const getAllComponentNamesCallback = useCallback(() => {
    if (!treeData) {
      return [];
    }
    return getAllComponentNames(treeData);
  }, [treeData]);

  const treeRender = useMemo(() => {
    return (
      <PathProvider currentPath={basePath}>
        <ComponentEditorContext.Provider value={componentEditorContext}>
          <ModalEditorDisplay>
            <TreeView />
          </ModalEditorDisplay>
        </ComponentEditorContext.Provider>
      </PathProvider>
    );
  }, [basePath, componentEditorContext]);

  const treeDataContext = useMemo(() => {
    if (treeData === null) {
      return {
        treeData: {} as IndexableObject,
        setTreeData,
        getAllComponentNames: getAllComponentNamesCallback,
      };
    }
    return {
      treeData,
      setTreeData,
      getAllComponentNames: getAllComponentNamesCallback,
    };
  }, [getAllComponentNamesCallback, setTreeData, treeData]);

  const treeSaveContext = useMemo(
    () => ({
      saveChangesAtPath,
      removeAtPath,
      addGamePiece,
    }),
    [addGamePiece, removeAtPath, saveChangesAtPath],
  );

  if (allImages === null) {
    return <Typography className="project-main">Loading images...</Typography>;
  }

  if (treeData === null) {
    return <Typography className="project-main">Loading components...</Typography>;
  }

  return (
    <>
      <TreeDataContext.Provider value={treeDataContext}>
        <TreeModifyContext.Provider value={treeSaveContext}>{treeRender}</TreeModifyContext.Provider>
      </TreeDataContext.Provider>
    </>
  );
}

export default ComponentEditorTab;
